import { useQuery } from "@tanstack/react-query";
import ApiClient from "../client";
import { ENDPOINTS } from "../endpoints";
import { AreaBounds } from "../types";
import { queryKeys } from "../keys";


const client = new ApiClient(process.env.API_URL!, "app");

// Queries
export const useShipsInArea = (bounds: AreaBounds, enabled = true) =>
    useQuery({
        queryKey: queryKeys.marineShipsArea(bounds),
        queryFn: () => {
            const params = new URLSearchParams();
            Object.entries(bounds).forEach(([key, value]) => {
                params.append(key, value.toString());
            });
            return client.get(`${ENDPOINTS.MARINE_SHIPS_AREA}?${params}`);
        },
        enabled,
    });

export const useShipPoiEvents = (
    shipId: number,
    limit: number = 20,
    enabled = true
) =>
    useQuery({
        queryKey: queryKeys.marineShipEvents(shipId, "poi"),
        queryFn: () => client.get(ENDPOINTS.MARINE_SHIP_POI_EVENTS(shipId), { params: { limit } }),
        enabled: enabled && !!shipId,
    });

export const useShipVisits = (
    shipId: number,
    limit: number = 20,
    enabled = true
) =>
    useQuery({
        queryKey: queryKeys.marineShipEvents(shipId, "visits"),
        queryFn: () => client.get(ENDPOINTS.MARINE_SHIP_VISITS(shipId), { params: { limit } }),
        enabled: enabled && !!shipId,
    });

export const useShipPortEvents = (
    shipId: number,
    limit: number = 20,
    enabled = true
) =>
    useQuery({
        queryKey: queryKeys.marineShipEvents(shipId, "port"),
        queryFn: () => client.get(ENDPOINTS.MARINE_SHIP_PORT_EVENTS(shipId), { params: { limit } }),
        enabled: enabled && !!shipId,
    });

export const usePoisInArea = (bounds: AreaBounds, enabled = true) =>
    useQuery({
        queryKey: queryKeys.marinePoisArea(bounds),
        queryFn: () => {
            const params = new URLSearchParams();
            Object.entries(bounds).forEach(([key, value]) => {
                params.append(key, value.toString());
            });
            return client.get(`${ENDPOINTS.MARINE_POIS_AREA}?${params}`);
        },
        enabled,
    });

export const useShipPositionHistory = (
    shipId: number,
    filters: { from?: string; to?: string },
    enabled = true
) =>
    useQuery({
        queryKey: queryKeys.marineShipPositionHistory(shipId, filters),
        queryFn: () => {
            const params = new URLSearchParams();
            if (filters.from) params.append("from", filters.from);
            if (filters.to) params.append("to", filters.to);
            const queryString = params.toString();
            return client.get(
                `${ENDPOINTS.MARINE_SHIP_POSITION_HISTORY(shipId)}${queryString ? `?${queryString}` : ""}`
            );
        },
        enabled: enabled && !!shipId,
    });

export const useShipByImoWithPositions = (
    imo: number,
    enabled = true
) =>
    useQuery({
        queryKey: ["marine", "ship", "imo", imo],
        queryFn: () => client.get(ENDPOINTS.MARINE_SHIP_LATEST_POSITION(imo)),
        enabled: enabled && !!imo,
    });
